import VideoHelper from './VideoHelper';
import CookieHelper from './CookieHelper';

/**
 * @typedef {object} YoutubeVideoSettings
 * @property {string} youtubeId YouTube video id
 * @property {HTMLElement} container Element that hosts the player
 * @property {string} [wrapperID] DOM id for the YT.Player mount point
 * @property {boolean} [hideControls] Minimal / looping embed when true
 * @property {boolean} [audio] When false, player is muted and autoplayed
 */

/**
 * YouTube IFrame API integration, lightbox players, and consent UI helpers.
 */
class YoutubeHelper {
    /**
     * Track whether the YouTube IFrame API script has been injected.
     */
    constructor() {
        this.youtubeApiLoaded = false;
    }

    /**
     * Create a YouTube IFrame player for the given settings.
     *
     * @param {YoutubeVideoSettings} videoSettings Player configuration
     * @returns {YT.Player}
     */
    static createPlayer(videoSettings) {
        const settings = {
            rel: 0,
            loop: 1,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.href,
            mute: !videoSettings.audio,
            volume: 0,
        };

        if (videoSettings.hideControls) {
            settings.controls = 0;
            settings.showinfo = 0; // Hide the video title
            settings.wmode = 'transparent';
            settings.modestbranding = 1; // Hide the Youtube Logo
            settings.playlist = videoSettings.youtubeId;
            settings.fs = 0; // Hide the full screen button
            settings.autohide = 1; // Hide video controls when playing
            settings.autoplay = 1;
        }

        return new YT.Player(videoSettings.wrapperID, {
            videoId: videoSettings.youtubeId,
            // host: 'https://www.youtube-nocookie.com',
            playerVars: settings,
            events: {
                onReady: (event) => {
                    if (!videoSettings.audio) {
                        VideoHelper.stopAll(videoSettings.wrapperID);
                        event.target.playVideo();
                        event.target.mute();
                    }
                },
                onStateChange: (event) => {
                    if (event && event.data === 1) { // playing
                        const videoHolder = document.getElementById(videoSettings.wrapperID);
                        if (videoHolder && videoHolder.id) {
                            const videoElement = videoHolder.parentElement.parentElement;
                            videoElement.classList.remove('video--loading', 'video--loading-animation');
                        }
                    } else if (event && event.data === 0) { // ended
                        event.target.playVideo();
                    } else if (event && event.data === -1) { // not started
                        if (!videoSettings.audio) {
                            event.target.mute();
                        }
                    }
                },
            },
        });
    }

    /**
     * Ensure the IFrame API is ready, then create and autoplay a player.
     *
     * @param {YoutubeVideoSettings} videoSettings Player configuration
     */
    static addAndAutoplayVideo(videoSettings) {
        const apiReady = document.body.getAttribute('data-youtube-api-ready');

        if (typeof apiReady !== typeof undefined && apiReady !== null && apiReady !== false) {
            YoutubeHelper.createPlayerContainer(videoSettings);
        } else {
            YoutubeHelper.addYoutubeIframeApi();
            window.onYouTubeIframeAPIReady = () => {
                document.body.setAttribute('data-youtube-api-ready', 'true');

                YoutubeHelper.createPlayerContainer(videoSettings);
            };
        }
    }

    /**
     * Mount a player into `videoSettings.container` and store it on `window.youtubePlayers`.
     *
     * @param {YoutubeVideoSettings} videoSettings Player configuration
     */
    static createPlayerContainer(videoSettings) {
        const videoContainer = videoSettings.container;
        const playerID = videoContainer.id;
        videoContainer.innerHTML = '';

        videoSettings.wrapperID = playerID;

        const player = YoutubeHelper.createPlayer(videoSettings);
        window.youtubePlayers[playerID] = player;
    }

    /**
     * Create and autoplay a YouTube player inside a lightbox container.
     *
     * @param {string} container Class name of the lightbox content root
     * @param {string} youtubeId YouTube video id
     * @param {string} wrapperID DOM id for the player mount point
     * @returns {YT.Player|object}
     */
    addAndAutoplayLightboxVideo(container, youtubeId, wrapperID) {
        let player = {};
        const apiReady = document.body.getAttribute('data-youtube-api-ready');
        const lightboxContent = document.getElementsByClassName(container)[0];

        if (typeof apiReady !== typeof undefined && apiReady !== null && apiReady !== false) {
            player = this.createLightboxPlayerContent(lightboxContent, wrapperID, youtubeId);
        } else {
            this.addYoutubeIframeApi();
            window.onYouTubeIframeAPIReady = () => {
                document.body.setAttribute('data-youtube-api-ready', 'true');

                player = this.createLightboxPlayerContent(lightboxContent, wrapperID, youtubeId);
            };
        }
        return player;
    }

    /**
     * Append a player mount node to lightbox content and create the player.
     *
     * @param {HTMLElement} lightboxContent Lightbox content container
     * @param {string} wrapperID DOM id for the player mount point
     * @param {string} youtubeId YouTube video id
     * @returns {YT.Player}
     */
    createLightboxPlayerContent(lightboxContent, wrapperID, youtubeId) {
        const videoContainer = document.createElement('div');

        videoContainer.id = wrapperID;
        lightboxContent.append(videoContainer);

        const player = this.createPlayer(wrapperID, youtubeId);

        window.lightboxPlayer = player;

        // window.addEventListener('lightboxclose', YoutubeHelper.stopAndDestroyPlayer);

        return player;
    }

    /**
     * Extract a YouTube video id from common URL formats.
     *
     * @param {string} url YouTube URL
     * @returns {string|string[]}
     */
    static youTubeGetID(url) {
        url = url.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
        let ID = url;

        if (url[2] !== undefined) {
            ID = url[2].split(/[^0-9a-z_-]/i);
            [ID] = ID;
        }
        return ID;
    }

    /**
     * Inject the YouTube IFrame API script once.
     */
    static addYoutubeIframeApi() {
        if (!this.youtubeApiLoaded) {
            const tag = document.createElement('script');

            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
        this.youtubeApiLoaded = true;
    }

    /**
     * Pause all YouTube players registered on `window.youtubePlayers`.
     */
    static stopAll() {
        if (window.youtubePlayers.length) {
            for (let i = 0; i < window.youtubePlayers.length; i += 1) {
                if (window.youtubePlayers[0].a !== null) {
                    window.youtubePlayers[0].pauseVideo();
                }
            }
        }
    }

    /**
     * Show the consent layer on a video (when still loading).
     *
     * @param {HTMLElement} video `.video` root element
     */
    static activateConsent(video) {
        if (video.classList.contains('video--loading')) {
            video.classList.remove('video--consent-accepted');
        }
    }

    /**
     * Mark a video as consent-accepted (hide consent layer).
     *
     * @param {HTMLElement} video `.video` root element
     */
    static displayYoutubeVideo(video) {
        video.classList.add('video--consent-accepted');
    }

    /**
     * Dispatch the `consent-type-youtube` custom event.
     */
    static dispatchConsentEvent() {
        const youtubeAllowedEvent = new CustomEvent('consent-type-youtube');
        window.dispatchEvent(youtubeAllowedEvent);
    }

    /**
     * Accept YouTube consent for all YouTube embeds on the page.
     */
    static displayAllYoutubeVideos() {
        CookieHelper.setCookie('consent-type-youtube', true);
        const videos = document.querySelectorAll('.video');
        if (videos) {
            for (const video of videos) {
                const videoUrl = video.getAttribute('data-embed');
                if (videoUrl && videoUrl.length) {
                    if (videoUrl.indexOf('youtube') >= 0) {
                        YoutubeHelper.displayYoutubeVideo(video);
                    }
                }
            }
        }
        window.removeEventListener('consent-type-youtube', YoutubeHelper.displayAllYoutubeVideos);
        window.addEventListener('consent-withdraw-youtube', YoutubeHelper.activateAllYoutubeConsents);
    }

    /**
     * Withdraw YouTube consent and re-show consent layers on YouTube embeds.
     */
    static activateAllYoutubeConsents() {
        CookieHelper.clearCookie('consent-type-youtube');
        const videos = document.querySelectorAll('.video');
        if (videos) {
            for (const video of videos) {
                const videoUrl = video.getAttribute('data-embed');
                if (videoUrl && videoUrl.length) {
                    if (videoUrl.indexOf('youtube') >= 0) {
                        YoutubeHelper.activateConsent(video);
                    }
                }
            }
        }
        window.addEventListener('consent-type-youtube', YoutubeHelper.displayAllYoutubeVideos);
        window.removeEventListener('consent-withdraw-youtube', YoutubeHelper.activateAllYoutubeConsents);
    }

    /**
     * Register listeners for YouTube consent grant/withdraw custom events.
     */
    static addYoutubeConsentListener() {
        window.addEventListener('consent-type-youtube', YoutubeHelper.displayAllYoutubeVideos, false);
        window.addEventListener('consent-withdraw-youtube', YoutubeHelper.activateAllYoutubeConsents);
    }
}

export default YoutubeHelper;
